import { useOne, useUpdate } from 'admin';
import type { Deal } from 'api';
import { logger } from 'monitor';
import { useMemo } from 'react';
import { DetailsList, Link, Skeleton, Stack, Typography } from 'ui';

import { DealInspectionsList } from '~/inspection';
import { RiskSelect } from '~/risk';

export const DealDetails = ({ dealId }: { dealId: number }) => {
  const { data: deal, isFetching } = useOne<Deal>({
    id: dealId,
    resource: '/deals/v1',
  });

  const { mutate } = useUpdate<Deal>();

  const detailsFields = useMemo(
    () => [
      {
        title: 'Loads',
        value: deal?.data.loads_count,
      },
      {
        title: 'Material',
        value: deal?.data.material_full_description,
      },
      {
        title: 'Producer',
        value: deal?.data.purchase_order.producer.name,
      },
      {
        title: 'End Processor',
        value: deal?.data.sales_order.end_processor.name,
      },
      {
        title: 'Loading Site',
        value: deal?.data.purchase_order.collection_address.full_address,
      },
      {
        title: 'Destination',
        value: deal?.data.sales_order.final_destination_address?.full_address,
      },
      {
        title: 'Risk',
        value: (
          <RiskSelect
            currentValue={deal?.data.risk_score || null}
            onChange={(riskScore) => {
              if (!deal?.data.id) {
                logger.error('Deal id missing during risk score update', deal);
                return;
              }
              mutate({
                id: deal?.data.id,
                invalidates: ['all'],
                resource: '/deals/v1',
                values: {
                  risk_score: riskScore,
                },
              });
            }}
          />
        ),
      },
      {
        title: 'Inspections',
        value: deal && <DealInspectionsList dealId={deal.data.id} />,
      },
    ],
    [deal, mutate]
  );

  if (isFetching || !deal) {
    return (
      <>
        {Array.from({ length: detailsFields.length }).map((_, index) => (
          <Skeleton key={index} height={60} />
        ))}
      </>
    );
  }

  const slug = deal.data.purchase_order.slug;
  return (
    <div>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={2}>
        <Typography variant="h5" component={Link} href={deal.data.gc_url} underline="none">
          Deal {dealId}
        </Typography>
        {slug !== null && (
          <Typography
            data-testid="deal-slug"
            variant="body1"
            component={Link}
            href={deal.data.purchase_order.gc_url}
            underline="none"
          >
            {slug}
          </Typography>
        )}
      </Stack>

      <DetailsList details={detailsFields} />
    </div>
  );
};
